import { Alert } from '@mui/joy';
import styles from './SubscriptionList.module.css';
import Spinner from 'components/global/Spinner/Spinner';
import SubscriptionItem from 'components/pages/Subscriptions/SubscriptionList/SubscriptionItem/SubscriptionItem';
import { useTranslation } from 'react-i18next';
import type {
  SubscriberModel,
  SubscriptionModel,
} from 'redux/services/api.generated';
import { useListSubscriptionsQuery } from 'redux/services/api.generated';
import { AlertTriangleIcon } from 'lucide-react';
import SubscriptionFilters from '../SubscriptionFilters/SubscriptionFilters';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SubscriptionStatuses } from 'components/pages/Subscriptions/subscriptionTypes';
import { initialSubscriptionStatuses } from 'components/pages/Subscriptions/subscriptionStatusDefaults';

// The key under which we store subscription statuses in local storage.
const subscriptionStatusesKey = 'sm-subscription-statuses';

interface SubscriptionListProps {
  selectedSubscriber: SubscriberModel | undefined;
}

function SubscriptionList({ selectedSubscriber }: SubscriptionListProps) {
  const { t } = useTranslation();
  const subscriptionListRef = useRef<HTMLUListElement | null>(null);

  const { data, isError, isFetching, isLoading, isUninitialized, refetch } =
    useListSubscriptionsQuery(
      {
        subscriberId: selectedSubscriber?.id as string,
      },
      { skip: !selectedSubscriber?.id },
    );

  let localStorageStatuses = initialSubscriptionStatuses;

  try {
    // JSON.parse will throw an exception if the JSON is invalid.
    if (localStorage.getItem(subscriptionStatusesKey)) {
      localStorageStatuses = JSON.parse(
        localStorage.getItem(subscriptionStatusesKey) as string,
      ) as SubscriptionStatuses;
    }
  } catch {
    console.error('JSON parsing failed');
  }

  const [statuses, setStatuses] =
    useState<SubscriptionStatuses>(localStorageStatuses);

  const filteredSubscriptions = useMemo(
    () =>
      data?.filter(({ subscriptionStatus }) => statuses[subscriptionStatus]) || [],
    [data, statuses],
  );

  useEffect(() => {
    localStorage.setItem(subscriptionStatusesKey, JSON.stringify(statuses));
  }, [statuses]);

  useEffect(() => {
    const list = subscriptionListRef.current;
    if (!list) {
      return;
    }

    // Set tabIndex to 0, when a list item is clicked.
    list.querySelectorAll('li').forEach((listItem) => {
      listItem.addEventListener('click', () => {
        const target = listItem;
        listItem.parentElement?.childNodes.forEach((child) => {
          // eslint-disable-next-line no-param-reassign
          (child as HTMLLIElement).tabIndex = -1;
        });
        target.tabIndex = 0;
      });
    });

    const subscriptionItemKeyboardHandler = (event: KeyboardEvent) => {
      const activeListItem: HTMLLIElement | null =
        list.querySelector('li[tabindex="0"]');
      if (!activeListItem) {
        return;
      }

      let nextListItem: HTMLLIElement | null = null;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        nextListItem = activeListItem.nextSibling as HTMLLIElement | null;
        if (!nextListItem) {
          nextListItem = list.querySelector('li:first-child');
        }
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        nextListItem = activeListItem.previousSibling as HTMLLIElement | null;
        if (!nextListItem) {
          nextListItem = list.querySelector('li:last-child');
        }
      }
      if (event.key === 'Home') {
        nextListItem = list.querySelector('li:first-child');
      }
      if (event.key === 'End') {
        nextListItem = list.querySelector('li:last-child');
      }

      if (nextListItem) {
        activeListItem.tabIndex = -1;
        nextListItem.tabIndex = 0;
        nextListItem.focus();
      }
    };

    list.addEventListener('keydown', subscriptionItemKeyboardHandler);
    return () => {
      list.removeEventListener('keydown', subscriptionItemKeyboardHandler);
    };
  }, [filteredSubscriptions]);

  if (isUninitialized) {
    return null;
  }

  if (isLoading) {
    return <Spinner size="large" position="centerAbsolute" />;
  }

  if (isError) {
    return (
      <Alert
        color="danger"
        size="lg"
        className={styles.alert}
        startDecorator={<AlertTriangleIcon />}
      >
        <p className="sd-text-lg">{t('subscriptions.fetchError')}</p>
      </Alert>
    );
  }

  return (
    <section>
      <div className={styles.topRow}>
        <h2 className="sd-heading-md" id="subscriptionsHeading">
          {t('subscriptions.subscriptions')} – {selectedSubscriber?.name}
        </h2>
        {data && data.length > 0 ? (
          <SubscriptionFilters statuses={statuses} setStatuses={setStatuses} />
        ) : null}
      </div>
      {data?.length ? (
        <>
          <p className={styles.visibleSubscriptionsMessage}>
            {t('subscriptions.visibleSubscriptionsCount', {
              filteredCount: filteredSubscriptions.length,
              totalCount: data.length,
            })}
          </p>
          <ul ref={subscriptionListRef} aria-labelledby="subscriptionsHeading">
            {filteredSubscriptions
              .slice()
              .sort((a, b) => a.subscriptionId.localeCompare(b.subscriptionId))
              .map((subscription, index) => (
                <SubscriptionItem
                  subscription={subscription as SubscriptionModel}
                  key={subscription.subscriptionId}
                  index={index}
                  refetchSubscriptions={() => refetch()}
                  isFetching={isFetching}
                />
              ))}
          </ul>
        </>
      ) : (
        <p>{t('subscriptions.noSubscriptionsFound')}</p>
      )}
    </section>
  );
}

export default SubscriptionList;
